import {IconButton} from '@wandb/components';
import React, {useCallback, useMemo} from 'react';

import {usePanelBankSectionsPagingContext} from '../../state/panelbank/PanelBankSectionsPagingContext';
import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../state/views/panelBankSectionConfig/actions';
import * as PanelBankSectionConfigTypes from '../../state/views/panelBankSectionConfig/types';
import {getPagingParams} from '../../util/panelbankFlow';
import {useFullFlowConfig} from '../../util/sectionFlowConfig';
import {PanelBankSectionPaginationMenu} from './PanelBankSectionPaginationMenu';
import {usePanelBankWidthContext} from './PanelBankWidthContext';

export const PanelBankSectionPagination = ({
  activePanelCount,
  isSectionNameSearchMatch,
  panelBankSectionConfigRef,
  panelSearchMatchCount,
  sectionLayoutType,
}: {
  activePanelCount: number;
  isSectionNameSearchMatch: boolean;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  panelSearchMatchCount: number;
  sectionLayoutType: 'grid' | 'flow';
}) => {
  const {
    currentPage,
    flowConfig,
    maxPage,
    panelsPerPage,
    setCurrentPage,
    startPanelIndex,
    updateFlowConfig,
    numTotalPanelsInSection,
  } = usePanelBankSectionPagination({
    activePanelCount,
    isSectionNameSearchMatch,
    panelBankSectionConfigRef,
    panelSearchMatchCount,
  });

  // If it's custom layout, we don't need pagination
  if (
    sectionLayoutType === 'grid' ||
    numTotalPanelsInSection <= flowConfig.columnsPerPage
  ) {
    return null;
  }

  return (
    <div
      className="flex items-center"
      data-test="panel-bank-section-pagination">
      <PanelBankSectionPaginationMenu
        flowConfig={flowConfig}
        panelsPerPage={panelsPerPage}
        startPanelIndex={startPanelIndex}
        updateFlowConfig={updateFlowConfig}
        numTotalPanelsInSection={numTotalPanelsInSection}
      />
      <IconButton
        isDisabled={currentPage === 0}
        aria-label="Previous page"
        icon="chevron-back"
        onPress={() => {
          setCurrentPage(currentPage - 1);
        }}
        size="md"
        variant="ghost"
      />
      <IconButton
        isDisabled={currentPage === maxPage}
        aria-label="Next page"
        icon="chevron-next"
        onPress={() => {
          setCurrentPage(currentPage + 1);
        }}
        size="md"
        variant="ghost"
      />
    </div>
  );
};

export const usePanelBankSectionPagination = ({
  activePanelCount,
  isSectionNameSearchMatch,
  panelBankSectionConfigRef,
  panelSearchMatchCount,
}: {
  activePanelCount: number;
  isSectionNameSearchMatch: boolean;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  panelSearchMatchCount: number;
}) => {
  const {isSearching} = usePanelSearchDebouncedContext();
  const numTotalPanelsInSection = isSearching
    ? isSectionNameSearchMatch
      ? activePanelCount
      : panelSearchMatchCount
    : activePanelCount;

  const {currentPageBySectionRefID, setCurrentPageForSectionRefID} =
    usePanelBankSectionsPagingContext();
  const setCurrentPage = useCallback(
    (newCurrentPage: number) => {
      setCurrentPageForSectionRefID(
        panelBankSectionConfigRef.id,
        newCurrentPage
      );
    },
    [panelBankSectionConfigRef, setCurrentPageForSectionRefID]
  );

  const sectionConfig = ViewHooks.usePart(panelBankSectionConfigRef);
  const flowConfig = useFullFlowConfig(
    sectionConfig.name,
    sectionConfig.flowConfig
  );
  const updateFlowConfig = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.updateFlowConfig
  );

  const {panelBankWidth} = usePanelBankWidthContext();

  const {panelsPerPage, panelsPerRow, maxPage} = useMemo(() => {
    return getPagingParams({
      panelBankWidth,
      panelCount: numTotalPanelsInSection,
      flowConfig,
    });
  }, [panelBankWidth, numTotalPanelsInSection, flowConfig]);

  const currentPage = useMemo(
    () => currentPageBySectionRefID[panelBankSectionConfigRef.id] ?? 0,
    [panelBankSectionConfigRef.id, currentPageBySectionRefID]
  );

  const startPanelIndex = currentPage * panelsPerPage;

  return useMemo(
    () => ({
      currentPage,
      flowConfig,
      maxPage,
      numTotalPanelsInSection,
      panelsPerPage,
      panelsPerRow,
      setCurrentPage,
      startPanelIndex,
      updateFlowConfig,
    }),
    [
      currentPage,
      flowConfig,
      maxPage,
      numTotalPanelsInSection,
      panelsPerPage,
      panelsPerRow,
      setCurrentPage,
      startPanelIndex,
      updateFlowConfig,
    ]
  );
};
